import React, {useState} from "react";
import {Popconfirm, Tooltip} from "antd";
import {EditOutlined, DeleteOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {
    TableContainer,
    ActionButtons,
    StyledVariationList,
    VariationItem,
    VariationDetail
} from "./ResponsiveDataTable.styles";
import {useHistory} from "react-router-dom";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import moment from "moment";
import {useStore} from "../../../../hooks/useStore";
import {UpdateEquipmentModal} from "../modals/updateEquipmentModal/UpdateEquipmentModal";
import {useObserver} from "mobx-react-lite";
import {ResponsiveDataTableStyled} from "components/_defaultComponents/ResponsiveDataTable";

const TABLE_PREFIX = "equipmentsTable";
const ResponsiveDataTable = ({
    data,
    loading,
    currentPage,
    setCurrentPage,
    dataCount,
    recall = () => {}
}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const stores = useStore();
    const [selectedRow, setSelectedRow] = useState(null);
    const [showUpdateEquipmentModal, setShowUpdateEquipmentModal] = useState(false);

    const openUpdateEquipmentModal = () => {
        setShowUpdateEquipmentModal(true);
    };
    const closeUpdateEquipmentModal = () => {
        setShowUpdateEquipmentModal(false);
    };

    const onClickUpdateButton = id => {
        const selectedItem = data.find(item => item?._id === id);
        setSelectedRow(selectedItem);
        openUpdateEquipmentModal();
    };

    const handleRemove = async ({id}) => {
        try {
            const response = await stores.equipmentStore.deleteEquipment({id});
            if (response) {
                recall();
            }
        } catch (e) {
            console.log(e);
        }
    };
    const renderActionButtons = id => {
        const permisions = {
            edit: true,
            delete: true
        };
        return (
            <ActionButtons>
                {permisions.edit && (
                    <Tooltip title={t("actions.edit", {ns: "common"})}>
                        <CustomButton
                            data-testid="editButton"
                            onClick={() => onClickUpdateButton(id)}
                            icon={<EditOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}
                {permisions.delete && (
                    <Tooltip title={t("actions.delete", {ns: "common"})} placement="bottom">
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={() => handleRemove({id})}
                            onCancel={() => setOpen(false)}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <CustomButton
                                data-testid="deleteButton"
                                icon={<DeleteOutlined />}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    return useObserver(() => (
        <>
            <ResponsiveDataTableStyled
                className={TABLE_PREFIX}
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowKey="_id"
                rowClassName="hello"
                dataSource={data.map(d => ({
                    ...d,
                    equipmentType: stores.equipmentStore.getEquipmentTypeById(d.typeId)
                }))}
                data-testid="dataTable"
                loading={loading || stores.equipmentStore.equipmentTypes.length === 0}
            >
                <TableContainer.Column
                    dataIndex="name"
                    title={"Ekipman Adı"}
                    render={name => (
                        <span data-testid="name" data-label={`Ekipman Adı:`}>
                            {name}
                        </span>
                    )}
                    sorter={(a, b) => a?.name?.localeCompare(b?.name, "tr")}
                    defaultSortOrder={"ascend"}
                    onCell={(record, index) => ({
                        "data-label": `Ekipman Adı`
                    })}
                />
                <TableContainer.Column
                    dataIndex="brand"
                    title={"Marka"}
                    render={brand => (
                        <span data-testid="brand" data-label={`Marka:`}>
                            {brand || "-"}
                        </span>
                    )}
                    onCell={(record, index) => ({
                        "data-label": `Marka`
                    })}
                />
                <TableContainer.Column
                    align="center"
                    dataIndex="equipmentType"
                    title={"Ekipman Tipi"}
                    render={equipmentType => (
                        <span data-testid="typeId" data-label={`Ekipman Tipi:`}>
                            {equipmentType?.name || "-"}
                        </span>
                    )}
                    sorter={(a, b) => a?.name?.localeCompare(b?.name, "tr")}
                    onCell={(record, index) => ({
                        "data-label": `Ekipman Tipi`
                    })}
                />
                <TableContainer.Column
                    align="center"
                    dataIndex="variations"
                    title={"Varyasyon Sayısı"}
                    render={variations => (
                        <span data-testid="typeId" data-label={`Varyasyon Sayısı:`}>
                            <Tooltip
                                overlayStyle={{maxWidth: "350px"}}
                                title={
                                    <StyledVariationList>
                                        {variations?.map((item, index) => (
                                            <VariationItem key={index}>
                                                <VariationDetail>
                                                    <span className="label">Renk:</span>
                                                    <span className="value">
                                                        {item?.color || "-"}
                                                    </span>
                                                </VariationDetail>
                                                <VariationDetail>
                                                    <span className="label">Beden:</span>
                                                    <span className="value">
                                                        {item?.size || "-"}
                                                    </span>
                                                </VariationDetail>
                                                <VariationDetail>
                                                    <span className="label">Fiyat:</span>
                                                    <span className="value">
                                                        {item?.price || "0"} ₺
                                                    </span>
                                                </VariationDetail>
                                                <VariationDetail>
                                                    <span className="label">External ID:</span>
                                                    <span className="value">
                                                        {item?.externalId || "-"}
                                                    </span>
                                                </VariationDetail>
                                            </VariationItem>
                                        ))}
                                    </StyledVariationList>
                                }
                            >
                                {
                                    <span style={{cursor: "pointer", textDecoration: "underline"}}>
                                        {variations?.length || "0"}
                                    </span>
                                }
                            </Tooltip>
                        </span>
                    )}
                    sorter={(a, b) => a?.variations?.length - b?.variations?.length}
                    onCell={(record, index) => ({
                        "data-label": `Varyasyon Sayısı`
                    })}
                />
                <TableContainer.Column
                    dataIndex="createdAt"
                    title={"Oluşturulma Tarihi"}
                    sorter={(a, b) => moment(a.createdAt).diff(moment(b.createdAt))}
                    render={createdAt => (
                        <span data-testid="createdAt" data-label={`Oluşturulma Tarihi:`}>
                            {createdAt ? moment(createdAt).format("DD.MM.YYYY HH:mm") : "-"}
                        </span>
                    )}
                    onCell={(record, index) => ({
                        "data-label": `Oluşturulma Tarihi`
                    })}
                />
                <TableContainer.Column
                    dataIndex="updatedAt"
                    title={"Son Güncellenme Tarihi"}
                    render={(updatedAt, record) => (
                        <span data-testid="updatedAt" data-label={`Son Güncellenme Tarihi:`}>
                            {updatedAt
                                ? moment(updatedAt).format("DD.MM.YYYY HH:mm")
                                : record?.createdAt
                                ? moment(record.createdAt).format("DD.MM.YYYY HH:mm")
                                : "-"}
                        </span>
                    )}
                    onCell={(record, index) => ({
                        "data-label": `Son Güncellenme Tarihi`
                    })}
                />
                <TableContainer.Column
                    dataIndex="externalId"
                    title={"External ID"}
                    render={externalId => (
                        <span data-testid="externalId" data-label={`External ID:`}>
                            {externalId || "-"}
                        </span>
                    )}
                    onCell={(record, index) => ({
                        "data-label": `External ID`
                    })}
                />
                <TableContainer.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                    onCell={(record, index) => ({
                        "data-label": `Aksiyon`
                    })}
                />
            </ResponsiveDataTableStyled>
            {/* Modals */}
            <UpdateEquipmentModal
                visibility={showUpdateEquipmentModal}
                equipment={selectedRow}
                recall={recall}
                changeVisibility={setShowUpdateEquipmentModal}
                onCancel={closeUpdateEquipmentModal}
                onOk={closeUpdateEquipmentModal}
            />
        </>
    ));
};

export default ResponsiveDataTable;
